import type {
  GetLegacyServicePickersResponse,
  GetPickersResponse,
} from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'

import type {
  Option,
  Step,
} from '~/scopes/product-core/components/presentationals/CustomizationFunnel/utils/types'
import { mapStep } from '~/scopes/product-core/components/presentationals/CustomizationFunnel/utils/utils'
import type {
  CondensedGuidance,
  CondensedOption,
  CondensedStep,
  PickerDisplayConfig,
} from '~/scopes/product-core/components/presentationals/CustomizationFunnelCondensed/CustomizationFunnelCondensed.types'

/**
 * Signature of `usePickerColConfig().getPickerDisplay`. Injected into the
 * pure factory so it never calls composables itself — only
 * `useCondensedFunnelData` resolves it from a Vue context.
 */
export type GetPickerDisplay = (
  pickerId: string,
  optionsCount?: number,
) => PickerDisplayConfig

type PickerGroup =
  | GetPickersResponse['pickerGroups'][number]
  | GetLegacyServicePickersResponse['pickerGroups'][number]

/**
 * [CondensedFunnel] Row titles use the API `label` first, with `sectionTitle`
 * kept as fallback — inverted vs the classic funnel `mapStep`.
 */
export function getCondensedTitle(group: PickerGroup): string {
  return group.label || (group.sectionTitle ?? '')
}

/**
 * Strips the fields discarded by the condensed experiment from a classic
 * option: good deal info and option illustrations.
 */
export function mapCondensedOption(option: Option): CondensedOption {
  const { goodDeal: _goodDeal, icon: _icon, ...condensedOption } = option

  return condensedOption
}

function mapCondensedGuidance(
  guidance: NonNullable<Step['guidance']>,
): CondensedGuidance {
  const { label, clickable, modalName, tracking } = guidance

  // [ToDo CondensedFunnel] the guidance card icon is intentionally excluded
  // until the new guidance card design lands with the pickers query param.
  return { label, clickable, modalName, tracking }
}

/**
 * Converts a classic funnel step into a condensed funnel step (row):
 * - drops desktop illustrations and columns
 * - drops guidance card icon/image
 * - maps side content for the grades step only (desktop carousel)
 * - resolves the picker display config (col config) for the row
 */
export function toCondensedStep(
  step: Step,
  getPickerDisplay: GetPickerDisplay,
): CondensedStep {
  return {
    id: step.id,
    title: step.title,
    titleEmphasis: step.titleEmphasis,
    titlePrice: step.titlePrice,
    trackingId: step.trackingId,
    type: step.type,
    showTelcoPickerBefore: step.showTelcoPickerBefore,
    guidance: step.guidance ? mapCondensedGuidance(step.guidance) : undefined,
    options: step.options.map(mapCondensedOption),
    display: getPickerDisplay(step.id, step.options.length),
    sideContent: step.id === 'grades' ? step.sideContentV2?.content : undefined,
  }
}

/**
 * Maps a variant picker group into a condensed funnel step (row).
 */
export function mapCondensedStep(
  product: GetProductResponse,
  group: GetPickersResponse['pickerGroups'][number],
  getPickerDisplay: GetPickerDisplay,
): CondensedStep {
  return {
    ...toCondensedStep(mapStep(product, group), getPickerDisplay),
    title: getCondensedTitle(group),
  }
}
